import type { GeneratedIntakeItem } from './types';

const DATABASE_FRACTION_DIGITS = 6;
const RFC3339_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|([+-])(\d{2}):(\d{2}))$/;

interface ParsedRevision {
  fractionalSecond: string;
  utcSecond: bigint;
}

export interface IntakeItemAuthorityEntry {
  item: GeneratedIntakeItem;
  pendingConfirmation: boolean;
  revision: string | null;
}

export type IntakeItemAuthority = Map<string, IntakeItemAuthorityEntry>;

export interface IntakeItemReconciliation {
  authority: IntakeItemAuthority;
  items: GeneratedIntakeItem[];
}

function parseRevision(revision: string): ParsedRevision | null {
  const match = RFC3339_PATTERN.exec(revision);
  if (!match) return null;

  const [, yearText, monthText, dayText, hourText, minuteText, secondText, fraction = ''] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  if (month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) return null;

  const local = new Date(0);
  local.setUTCFullYear(year, month - 1, day);
  local.setUTCHours(hour, minute, second, 0);
  if (
    local.getUTCFullYear() !== year ||
    local.getUTCMonth() !== month - 1 ||
    local.getUTCDate() !== day ||
    local.getUTCHours() !== hour ||
    local.getUTCMinutes() !== minute ||
    local.getUTCSeconds() !== second
  ) {
    return null;
  }

  let offsetSeconds = 0;
  if (match[8] !== 'Z') {
    const offsetHour = Number(match[10]);
    const offsetMinute = Number(match[11]);
    if (offsetHour > 23 || offsetMinute > 59) return null;
    const direction = match[9] === '+' ? 1 : -1;
    offsetSeconds = direction * (offsetHour * 60 + offsetMinute) * 60;
  }

  return {
    fractionalSecond: fraction.padEnd(DATABASE_FRACTION_DIGITS, '0'),
    utcSecond: BigInt(Math.trunc(local.getTime() / 1000) - offsetSeconds),
  };
}

/** Returns null when either revision is not a database-precision RFC 3339 timestamp. */
export function compareIntakeItemRevisions(left: string, right: string): -1 | 0 | 1 | null {
  if (left === right) return 0;
  const parsedLeft = parseRevision(left);
  const parsedRight = parseRevision(right);
  if (!parsedLeft || !parsedRight) return null;
  if (parsedLeft.utcSecond < parsedRight.utcSecond) return -1;
  if (parsedLeft.utcSecond > parsedRight.utcSecond) return 1;
  if (parsedLeft.fractionalSecond < parsedRight.fractionalSecond) return -1;
  if (parsedLeft.fractionalSecond > parsedRight.fractionalSecond) return 1;
  return 0;
}

function itemIdentity(item: GeneratedIntakeItem | null | undefined): string | null {
  return item?.intake_item_id ?? null;
}

function itemRevision(item: GeneratedIntakeItem): string | null {
  return item.updated_at ?? null;
}

export function storeMutationItem(
  authority: IntakeItemAuthority,
  item: GeneratedIntakeItem | null | undefined,
): IntakeItemAuthority {
  const itemId = itemIdentity(item);
  if (!itemId || !item) return authority;
  const next = new Map(authority);
  next.set(itemId, {
    item,
    pendingConfirmation: true,
    revision: itemRevision(item),
  });
  return next;
}

export function reconcileIntakeItems(
  dashboardItems: GeneratedIntakeItem[],
  authority: IntakeItemAuthority,
): IntakeItemReconciliation {
  const nextAuthority = new Map(authority);
  const seen = new Set<string>();
  const items = dashboardItems.map(dashboardItem => {
    const itemId = itemIdentity(dashboardItem);
    if (!itemId) return dashboardItem;
    seen.add(itemId);
    const existing = authority.get(itemId);
    if (!existing) {
      nextAuthority.set(itemId, {
        item: dashboardItem,
        pendingConfirmation: false,
        revision: itemRevision(dashboardItem),
      });
      return dashboardItem;
    }

    const dashboardRevision = itemRevision(dashboardItem);
    if (dashboardRevision === existing.revision) {
      nextAuthority.set(itemId, {
        item: existing.item,
        pendingConfirmation: false,
        revision: existing.revision,
      });
      return existing.item;
    }
    if (!dashboardRevision || !existing.revision) return existing.item;

    const ordering = compareIntakeItemRevisions(dashboardRevision, existing.revision);
    if (ordering === null || ordering < 0) return existing.item;
    if (ordering === 0) {
      nextAuthority.set(itemId, { ...existing, pendingConfirmation: false });
      return existing.item;
    }
    nextAuthority.set(itemId, {
      item: dashboardItem,
      pendingConfirmation: false,
      revision: dashboardRevision,
    });
    return dashboardItem;
  });

  for (const [itemId, entry] of authority) {
    if (!seen.has(itemId)) items.push(entry.item);
  }
  return { authority: nextAuthority, items };
}

export function intakeItemAuthoritiesEqual(
  left: IntakeItemAuthority,
  right: IntakeItemAuthority,
): boolean {
  if (left.size !== right.size) return false;
  for (const [itemId, leftEntry] of left) {
    const rightEntry = right.get(itemId);
    if (
      !rightEntry ||
      leftEntry.item !== rightEntry.item ||
      leftEntry.pendingConfirmation !== rightEntry.pendingConfirmation ||
      leftEntry.revision !== rightEntry.revision
    ) {
      return false;
    }
  }
  return true;
}
